import { Box, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { FormEvent, ReactNode } from "react";
import { AuthHeaderIcon, type AuthHeaderIconKey } from "./loginNoteIcons";

export const loginFormPaddingSx = {
  px: { xs: 3, sm: 4 },
  py: { xs: 3.5, sm: 4 },
} as const;

export function getLoginFieldSx(theme: Theme, marginBottom = 2.5) {
  return {
    mb: marginBottom,
    "& .MuiOutlinedInput-root": {
      transition: "box-shadow 0.18s ease",
      "&.Mui-focused": {
        boxShadow: `0 0 0 4px ${alpha(theme.palette.custom.orange, 0.12)}`,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.custom.orange,
        },
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.custom.orange,
    },
  } as const;
}

export function getLoginPrimaryButtonSx(theme: Theme) {
  return {
    py: 1.25,
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.9375rem",
    lineHeight: 1.3,
    whiteSpace: "normal",
    borderRadius: 1.5,
    boxShadow: `0 6px 20px -8px ${alpha(theme.palette.custom.preussiskBla, 0.55)}`,
    background: `linear-gradient(135deg, ${theme.palette.custom.preussiskBla} 0%, #1a3a6e 100%)`,
    transition: "transform 0.18s ease, box-shadow 0.18s ease",
    "&:hover": {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.custom.preussiskBla} 100%)`,
      boxShadow: `0 12px 28px -10px ${alpha(theme.palette.custom.preussiskBla, 0.7)}`,
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
    "&.Mui-disabled": {
      background: theme.palette.action.disabledBackground,
      boxShadow: "none",
    },
  } as const;
}

export function getLoginSecondaryLinkSx() {
  return {
    fontSize: "0.875rem",
    fontWeight: 500,
  } as const;
}

export function getLoginDividerFooterSx() {
  return {
    mt: 3,
    pt: 2,
    borderTop: 1,
    borderColor: "divider",
  } as const;
}

export function getLoginFooterLinkBoxSx() {
  return {
    mt: 2.5,
  } as const;
}

export type LoginFormHeaderProps = {
  title: string;
  subtitle?: string;
  headerIcon?: AuthHeaderIconKey;
  headerTestId?: string;
  titleTestId?: string;
  subtitleTestId?: string;
  headerIconTestId?: string;
};

export function LoginFormHeader({
  title,
  subtitle,
  headerIcon,
  headerTestId = "login-form-header",
  titleTestId = "login-form-title",
  subtitleTestId = "login-form-subtitle",
  headerIconTestId,
}: LoginFormHeaderProps) {
  return (
    <Box sx={{ mb: 3 }} data-testid={headerTestId}>
      <Typography
        variant="h5"
        component="h1"
        data-testid={titleTestId}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: 600,
          letterSpacing: "-0.02em",
          color: "text.primary",
          lineHeight: 1.25,
          minWidth: 0,
        }}
      >
        {headerIcon ? (
          <AuthHeaderIcon iconKey={headerIcon} testId={headerIconTestId} />
        ) : null}
        <Box component="span" sx={{ minWidth: 0 }}>
          {title}
        </Box>
      </Typography>
      {subtitle ? (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid={subtitleTestId}
          sx={{ mt: 1, lineHeight: 1.55, overflowWrap: "anywhere" }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

type LoginCardContentProps = {
  children: ReactNode;
  testId?: string;
  component?: "form" | "div";
  onSubmit?: (e: FormEvent) => void;
  noValidate?: boolean;
};

export function LoginCardContent({
  children,
  testId,
  component = "div",
  onSubmit,
  noValidate,
}: LoginCardContentProps) {
  return (
    <Box
      component={component}
      onSubmit={onSubmit}
      noValidate={noValidate}
      sx={{
        ...loginFormPaddingSx,
        textAlign: "left",
      }}
      data-testid={testId}
    >
      {children}
    </Box>
  );
}
